import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Code,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'
import { useOrderAddressDoubleCheck } from '@/hooks/useOrderConsistencyData'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import type { OrderListItem } from '@/types'
import { formatDateTime } from '@/utils/formatUtils'

type OrderAddressDoubleCheckModalProps = {
  isOpen: boolean
  onClose: () => void
  order: OrderListItem | null
}

type DetailFieldProps = {
  label: string
  value: string | null | undefined
  code?: boolean
}

function DetailField({ label, value, code = false }: DetailFieldProps) {
  const displayValue = value?.trim() || '-'
  return (
    <Box minW={0}>
      <Text fontSize="xs" color="gray.500" mb={1}>
        {label}
      </Text>
      {code && displayValue !== '-' ? (
        <Code
          display="block"
          fontSize="xs"
          overflowWrap="anywhere"
          whiteSpace="pre-wrap"
          p={2}
          borderRadius="md"
        >
          {displayValue}
        </Code>
      ) : (
        <Text fontSize="sm" overflowWrap="anywhere">
          {displayValue}
        </Text>
      )}
    </Box>
  )
}

function formatJsonSnapshot(value: string | null): string {
  if (!value?.trim()) {
    return '-'
  }
  try {
    return JSON.stringify(JSON.parse(value), null, 2)
  } catch {
    return value
  }
}

export function OrderAddressDoubleCheckModal({
  isOpen,
  onClose,
  order,
}: OrderAddressDoubleCheckModalProps) {
  const { t } = useTranslation()
  const { currentEnv } = useNetworkEnvironment()
  const orderId = order?.orderId ?? null
  const query = useOrderAddressDoubleCheck(orderId, { enabled: isOpen })
  const data = query.data
  const record = data?.record
  const displayMode = data?.mode ?? currentEnv

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>
          <HStack justify="space-between" pr={8}>
            <Text>{t('orders.addressDoubleCheck.title')}</Text>
            <Badge colorScheme={displayMode === 'testnet' ? 'orange' : 'green'}>
              {t(`orders.addressDoubleCheck.modes.${displayMode}`)}
            </Badge>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto">
          <VStack align="stretch" spacing={5}>
            <HStack justify="space-between" align="center">
              <HStack minW={0}>
                <Text fontSize="sm" fontWeight="semibold">
                  {t('orders.addressDoubleCheck.fields.orderId')}:
                </Text>
                <Code fontSize="sm" overflowWrap="anywhere">
                  {orderId ?? '-'}
                </Code>
              </HStack>
              <RefreshIconButton
                label={t('common.refresh')}
                onClick={() => query.refetch()}
                isLoading={query.isFetching}
              />
            </HStack>

            {query.isLoading && (
              <VStack align="stretch" spacing={3}>
                <Skeleton height="64px" />
                <Skeleton height="128px" />
                <Skeleton height="128px" />
              </VStack>
            )}

            {query.error && (
              <Alert status="error">
                <AlertIcon />
                {query.error instanceof Error
                  ? query.error.message
                  : t('orders.addressDoubleCheck.loadFailed')}
              </Alert>
            )}

            {!query.isLoading && !query.error && data && !record && (
              <Alert status="info">
                <AlertIcon />
                {t('orders.addressDoubleCheck.empty')}
              </Alert>
            )}

            {record && (
              <>
                <HStack>
                  <Text fontSize="sm" fontWeight="semibold">
                    {t('orders.addressDoubleCheck.fields.matchResult')}
                  </Text>
                  <Badge colorScheme={record.isMatched ? 'green' : 'red'}>
                    {record.isMatched
                      ? t('orders.addressDoubleCheck.matched')
                      : t('orders.addressDoubleCheck.mismatched')}
                  </Badge>
                </HStack>

                <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
                  <DetailField
                    label={t('orders.addressDoubleCheck.fields.recordId')}
                    value={record.id}
                    code
                  />
                  <DetailField
                    label={t(
                      'orders.addressDoubleCheck.fields.internalOrderId'
                    )}
                    value={record.internalOrderId}
                    code
                  />
                  <DetailField
                    label={t('orders.addressDoubleCheck.fields.chainId')}
                    value={record.chainId}
                    code
                  />
                  <DetailField
                    label={t('orders.addressDoubleCheck.fields.computedBy')}
                    value={record.computedBy}
                  />
                  <DetailField
                    label={t('orders.addressDoubleCheck.fields.computedAt')}
                    value={
                      record.computedAt
                        ? formatDateTime(record.computedAt)
                        : null
                    }
                  />
                  <DetailField
                    label={t('orders.addressDoubleCheck.fields.diffReason')}
                    value={record.diffReason}
                  />
                  <DetailField
                    label={t('orders.addressDoubleCheck.fields.createdAt')}
                    value={
                      record.createdAt ? formatDateTime(record.createdAt) : null
                    }
                  />
                  <DetailField
                    label={t('orders.addressDoubleCheck.fields.updatedAt')}
                    value={
                      record.updatedAt ? formatDateTime(record.updatedAt) : null
                    }
                  />
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                  <DetailField
                    label={t(
                      'orders.addressDoubleCheck.fields.offchainAddress'
                    )}
                    value={record.offchainAddress}
                    code
                  />
                  <DetailField
                    label={t('orders.addressDoubleCheck.fields.onchainAddress')}
                    value={record.onchainAddress}
                    code
                  />
                  <DetailField
                    label={t('orders.addressDoubleCheck.fields.factoryAddress')}
                    value={record.factoryAddress}
                    code
                  />
                  <DetailField
                    label={t('orders.addressDoubleCheck.fields.salt')}
                    value={record.salt}
                    code
                  />
                  <DetailField
                    label={t('orders.addressDoubleCheck.fields.initCodeHash')}
                    value={record.initCodeHash}
                    code
                  />
                  <DetailField
                    label={t('orders.addressDoubleCheck.fields.configHash')}
                    value={record.configHash}
                    code
                  />
                </SimpleGrid>

                <VStack align="stretch" spacing={4}>
                  <DetailField
                    label={t(
                      'orders.addressDoubleCheck.fields.saltConfigSnapshot'
                    )}
                    value={formatJsonSnapshot(record.saltConfigSnapshot)}
                    code
                  />
                  <DetailField
                    label={t(
                      'orders.addressDoubleCheck.fields.platformConfigSnapshot'
                    )}
                    value={formatJsonSnapshot(record.platformConfigSnapshot)}
                    code
                  />
                  <DetailField
                    label={t('orders.addressDoubleCheck.fields.metadata')}
                    value={formatJsonSnapshot(record.metadata)}
                    code
                  />
                </VStack>
              </>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
